import {useEffect, useState} from 'react';

import API from '../api/axios';
import {blobToText} from '../utils/blob';

export const PREVIEW_POLL_INTERVAL = 2000;
export const PREVIEW_TIMEOUT = 300000;

export type MapSourcePreview = {
  url: string | null;
  isPreparing: boolean;
  error: string | null;
};

const readBlobMessage = async (blob: Blob): Promise<string | null> => {
  try {
    const parsed = JSON.parse(await blobToText(blob));

    return typeof parsed?.message === 'string' ? parsed.message : null;
  } catch (error) {
    return null;
  }
};

export const useMapSourcePreview = (
  sourceFilename: string | null
): MapSourcePreview => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setUrl(null);
    setError(null);

    if (!sourceFilename) {
      return undefined;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined = undefined;
    let objectUrl: string | null = null;
    const startedAt = Date.now();

    const poll = async () => {
      let response;

      try {
        response = await API.get(`/map/source/${sourceFilename}/preview`, {
          responseType: 'blob',
          validateStatus: (status) =>
            status === 200 || status === 202 || status === 409,
        });
      } catch (requestError) {
        if (!cancelled) {
          setError('The preview could not be loaded');
        }

        return;
      }

      if (cancelled) {
        return;
      }

      if (response.status === 200) {
        objectUrl = URL.createObjectURL(response.data);
        setUrl(objectUrl);

        return;
      }

      if (response.status === 409) {
        const message = await readBlobMessage(response.data);

        if (!cancelled) {
          setError(message ?? 'The preview could not be generated');
        }

        return;
      }

      if (Date.now() - startedAt > PREVIEW_TIMEOUT) {
        setError('The preview is taking longer than expected');

        return;
      }

      timer = setTimeout(poll, PREVIEW_POLL_INTERVAL);
    };

    poll();

    return () => {
      cancelled = true;

      if (timer) {
        clearTimeout(timer);
      }

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [sourceFilename]);

  return {url, isPreparing: !!sourceFilename && !url && !error, error};
};
